import React, { useEffect, createContext, useContext } from "react";
import { CompanyFonts, FontConfig, ModuleFonts } from "./FontSettings";

interface FontContextValue {
  fonts: CompanyFonts;
}

const FontContext = createContext<FontContextValue>({ fonts: {} });

export const useFonts = () => useContext(FontContext);

interface FontProviderProps {
  fonts: CompanyFonts;
  children: React.ReactNode;
}

const FontProvider: React.FC<FontProviderProps> = ({ fonts, children }) => {
  useEffect(() => {
    // Remove existing font links
    const existingLinks = document.querySelectorAll('link[data-font-config="true"]');
    existingLinks.forEach((link) => link.remove());

    const loadedUrls = new Set<string>();

    // Load general font
    if (fonts.general?.googleFontsUrl && !loadedUrls.has(fonts.general.googleFontsUrl)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = fonts.general.googleFontsUrl;
      link.setAttribute("data-font-config", "true");
      document.head.appendChild(link);
      loadedUrls.add(fonts.general.googleFontsUrl);
    }

    // Load module and subsection fonts
    const moduleKeys = [
      "kanban",
      "documentation",
      "calendar",
      "tasks",
      "profiles",
      "email",
      "reports",
    ] as const;

    moduleKeys.forEach((key) => {
      const module = fonts[key] as ModuleFonts | undefined;

      // Load main module font
      if (module?.main?.googleFontsUrl && !loadedUrls.has(module.main.googleFontsUrl)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = module.main.googleFontsUrl;
        link.setAttribute("data-font-config", "true");
        document.head.appendChild(link);
        loadedUrls.add(module.main.googleFontsUrl);
      }

      // Load subsection fonts
      if (module?.subsections) {
        Object.values(module.subsections).forEach((subsection: FontConfig) => {
          if (subsection?.googleFontsUrl && !loadedUrls.has(subsection.googleFontsUrl)) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = subsection.googleFontsUrl;
            link.setAttribute("data-font-config", "true");
            document.head.appendChild(link);
            loadedUrls.add(subsection.googleFontsUrl);
          }
        });
      }
    });

    // Set CSS variables
    const root = document.documentElement;

    // Set general font
    if (fonts.general?.fontFamily) {
      root.style.setProperty("--font-general", fonts.general.fontFamily);
      document.body.style.fontFamily = fonts.general.fontFamily;
    } else {
      root.style.removeProperty("--font-general");
    }

    // Set module and subsection fonts
    moduleKeys.forEach((key) => {
      const module = fonts[key] as ModuleFonts | undefined;
      const mainFont = module?.main?.fontFamily || fonts.general?.fontFamily;

      // Set main module font variable
      if (mainFont) {
        root.style.setProperty(`--font-${key}`, mainFont);
      } else {
        root.style.removeProperty(`--font-${key}`);
      }

      // Set subsection font variables
      if (module?.subsections) {
        Object.entries(module.subsections).forEach(([subsectionKey, subsectionFont]) => {
          const fontFamily = subsectionFont?.fontFamily || mainFont;
          if (fontFamily) {
            root.style.setProperty(`--font-${key}-${subsectionKey}`, fontFamily);
          } else {
            root.style.removeProperty(`--font-${key}-${subsectionKey}`);
          }
        });
      }
    });

    // Cleanup function
    return () => {
      const links = document.querySelectorAll('link[data-font-config="true"]');
      links.forEach((link) => link.remove());

      root.style.removeProperty("--font-general");
      moduleKeys.forEach((key) => {
        root.style.removeProperty(`--font-${key}`);
        
        // Clean up subsection variables
        const module = fonts[key] as ModuleFonts | undefined;
        if (module?.subsections) {
          Object.keys(module.subsections).forEach((subsectionKey) => {
            root.style.removeProperty(`--font-${key}-${subsectionKey}`);
          });
        }
      });
    };
  }, [fonts]);

  return (
    <FontContext.Provider value={{ fonts }}>
      {children}
    </FontContext.Provider>
  );
};

export default FontProvider;
export { FontContext };